import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface EstimateListItem {
  id: number;
  doc_no: string;
  issue_date: string;
  customer_name: string;
  project_name: string;
  total: number;
  status: string;
}

const statusColors: Record<string, string> = {
  draft: '#f0ad4e',
  issued: '#5bc0de',
  ordered: '#5cb85c',
  lost: '#d9534f',
};

const statusLabels: Record<string, string> = {
  draft: '下書き',
  issued: '発行済',
  ordered: '受注',
  lost: '失注',
};

const Estimate: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<EstimateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 検索条件
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchProject, setSearchProject] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');
  const [searchFrom, setSearchFrom] = useState('');
  const [searchTo, setSearchTo] = useState('');

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (searchCustomer) params.customer_name = searchCustomer;
      if (searchProject) params.project_name = searchProject;
      if (searchStatus !== 'all') params.status = searchStatus;
      if (searchFrom) params.issue_date_from = searchFrom;
      if (searchTo) params.issue_date_to = searchTo;

      const result = await window.api.estimate.list(params);
      setItems(result);
    } catch (err: any) {
      setError(err.message || 'データ取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [searchCustomer, searchProject, searchStatus, searchFrom, searchTo]);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('この見積を削除してもよろしいですか？')) return;
    try {
      await window.api.estimate.delete(id);
      fetchEstimates();
    } catch (err: any) {
      alert(err.message || '削除に失敗しました');
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      const newId = await window.api.estimate.duplicate(id);
      navigate(`/estimate/${newId}`);
    } catch (err: any) {
      alert(err.message || '複製に失敗しました');
    }
  };

  const handlePdf = async (id: number) => {
    try {
      const pdfPath = await window.api.estimate.generatePdf(id);
      alert(`PDFを出力しました: ${pdfPath}`);
    } catch (err: any) {
      alert(err.message || 'PDF出力に失敗しました');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', color: '#333', margin: 0 }}>見積一覧</h1>
        <button
          onClick={() => navigate('/estimate/new')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#337ab7',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          新規作成
        </button>
      </div>

      {/* 検索バー */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>顧客名</label>
          <input
            type="text"
            value={searchCustomer}
            onChange={(e) => setSearchCustomer(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', width: '150px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>件名</label>
          <input
            type="text"
            value={searchProject}
            onChange={(e) => setSearchProject(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', width: '150px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>ステータス</label>
          <select
            value={searchStatus}
            onChange={(e) => setSearchStatus(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', width: '120px' }}
          >
            <option value="all">全て</option>
            <option value="draft">下書き</option>
            <option value="issued">発行済</option>
            <option value="ordered">受注</option>
            <option value="lost">失注</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>期間(From)</label>
          <input
            type="date"
            value={searchFrom}
            onChange={(e) => setSearchFrom(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', width: '150px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>期間(To)</label>
          <input
            type="date"
            value={searchTo}
            onChange={(e) => setSearchTo(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', width: '150px' }}
          />
        </div>
        <button
          onClick={fetchEstimates}
          style={{
            padding: '6px 16px',
            backgroundColor: '#337ab7',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            height: '32px',
          }}
        >
          検索
        </button>
      </div>

      {/* エラー表示 */}
      {error && (
        <div style={{
          padding: '10px 15px',
          backgroundColor: '#f2dede',
          color: '#a94442',
          borderRadius: '4px',
          marginBottom: '15px',
        }}>
          {error}
        </div>
      )}

      {/* ローディング */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          読み込み中...
        </div>
      )}

      {/* テーブル */}
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: '#333' }}>見積No</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: '#333' }}>発行日</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: '#333' }}>顧客名</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: '#333' }}>件名</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#333' }}>合計</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '13px', color: '#333' }}>ステータス</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '13px', color: '#333' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  見積データがありません
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/estimate/${item.id}`)}
                  style={{
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9f9f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ padding: '10px 12px', fontSize: '13px' }}>{item.doc_no}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px' }}>{formatDate(item.issue_date)}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px' }}>{item.customer_name}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px' }}>{item.project_name}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', textAlign: 'right' }}>
                    ¥{item.total.toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      color: 'white',
                      backgroundColor: statusColors[item.status] || '#999',
                    }}>
                      {statusLabels[item.status] || item.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/estimate/${item.id}`); }}
                        style={actionButtonStyle}
                      >
                        編集
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(item.id); }}
                        style={actionButtonStyle}
                      >
                        複製
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePdf(item.id); }}
                        style={actionButtonStyle}
                      >
                        PDF
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                        style={{ ...actionButtonStyle, color: '#d9534f' }}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

const actionButtonStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: '11px',
  border: '1px solid #ccc',
  borderRadius: '3px',
  backgroundColor: 'white',
  cursor: 'pointer',
  color: '#333',
};

export default Estimate;
